import type { ReadingPositionValue } from '@xdocs/schemas';

/**
 * F66 offline resume-position fallback (E20 §5.10-3: "reader position" is
 * named explicitly as client-state that must survive backgrounding/close/
 * flaky-network). `reader.position.get/.set` are the server-authoritative
 * path; this is the local durable mirror used when the socket is down or
 * the initial fetch hasn't resolved yet, so a reader who opens the app
 * offline still resumes where they left off. Scoped to this feature
 * (`packages/ws-protocol`'s offline queue persistence is a different seam —
 * that durably replays the `reader.position.set` MUTATION on reconnect;
 * this store is a read-side cache for instant local resume).
 *
 * IndexedDB is used directly (no `idb` wrapper — not on the approved-libs
 * allowlist and this is a ~30-line surface); every method fails soft
 * (resolves `undefined`/no-throws) in non-browser/unsupported environments
 * (SSR, older browsers, private-mode storage denial) so a reader without
 * IndexedDB simply loses local-fallback resume, never crashes.
 */
export interface StoredReaderPosition {
  documentId: string;
  versionId: string;
  position: ReadingPositionValue;
  updatedAt: string;
}

const DB_NAME = 'xdocs-reader';
const DB_VERSION = 1;
const STORE_NAME = 'positions';

function isIndexedDbAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

function openDb(): Promise<IDBDatabase | null> {
  if (!isIndexedDbAvailable()) return Promise.resolve(null);
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'documentId' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export async function getStoredReaderPosition(documentId: string): Promise<StoredReaderPosition | undefined> {
  const db = await openDb();
  if (!db) return undefined;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const request = tx.objectStore(STORE_NAME).get(documentId);
      request.onsuccess = () => resolve((request.result as StoredReaderPosition | undefined) ?? undefined);
      request.onerror = () => resolve(undefined);
    } catch {
      resolve(undefined);
    } finally {
      db.close();
    }
  });
}

export async function setStoredReaderPosition(entry: StoredReaderPosition): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(entry);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    } finally {
      db.close();
    }
  });
}
